import { useEffect, useMemo, useState } from "react";
import { ServiceRowObj } from "../../variables/serviceData";
import { Order } from "./types";

const getOrderServiceValue = (order: Order | null | undefined, serviceKey: string) => {
  if (!order) return 0;
  if (serviceKey === "include_mail_hard_copy") {
    return Number(order.include_mail_hard_copy) === 1 || Number(order.include_2_day_shipping) === 1 ? 1 : 0;
  }
  return Number(order?.[serviceKey]) === 1 ? 1 : 0;
};

export function useEligibleProjectSelection(params: {
  orders: Order[];
  serviceGroup: ServiceRowObj["group"];
  serviceKey: string;
  perPage: number;
}) {
  const { orders, serviceGroup, serviceKey, perPage } = params;

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredOrders = useMemo(() => {
    const requiredType = serviceGroup === "Certified" ? "certified" : "standard";
    return (orders || []).filter((o) => String(o?.translation_type || "").toLowerCase() === requiredType);
  }, [orders, serviceGroup]);

  const eligibleOrders = useMemo(() => {
    return filteredOrders.filter((o) => getOrderServiceValue(o, serviceKey) === 0);
  }, [filteredOrders, serviceKey]);

  useEffect(() => {
    if (currentPage === 1) return;
    const maxPage = Math.max(1, Math.ceil(eligibleOrders.length / perPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [currentPage, eligibleOrders.length, perPage]);

  useEffect(() => {
    if (!selectedOrder) return;
    const stillEligible = eligibleOrders.some(
      (o) => String(o?.order_id) === String(selectedOrder?.order_id)
    );
    if (!stillEligible) setSelectedOrder(null);
  }, [eligibleOrders, selectedOrder]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(eligibleOrders.length / perPage));
  }, [eligibleOrders.length, perPage]);

  const pagedOrders = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return eligibleOrders.slice(start, start + perPage);
  }, [eligibleOrders, currentPage, perPage]);

  const orderCountForGroup = filteredOrders.length;

  return {
    selectedOrder,
    setSelectedOrder,
    currentPage,
    setCurrentPage,
    filteredOrders,
    eligibleOrders,
    totalPages,
    pagedOrders,
    orderCountForGroup,
  };
}
